'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Breadcrumb from '@/components/Breadcrumb';

const supabase = createClient();

interface KpiSnapshot {
  id: string;
  data_snapshot: string;
  totale_dispositivi: number;
  dispositivi_online: number;
  dispositivi_offline: number;
  ticket_aperti: number;
  ticket_risolti_oggi: number;
  sla_rispettati_percentuale: number;
  tempo_medio_risoluzione_ore: number;
  uptime_medio_percentuale: number;
  costo_manutenzione_mese: number;
  valore_parco_macchine: number;
  budget_residuo: number;
  allarmi_attivi: number;
}

interface ConformitaItem {
  id: string;
  tipo: string;
  anno: number;
  voce: string;
  conforme: boolean;
  note: string;
}

interface ReportConfig {
  id: string;
  nome: string;
  tipo: string;
  frequenza: string;
  formato: string;
  destinatari_email: string[];
  attivo: boolean;
  ultima_esecuzione: string | null;
}

interface PerformanceTecnico {
  id: string;
  tecnico_nome: string;
  ticket_risolti: number;
  ticket_assegnati: number;
  tempo_medio_risoluzione_ore: number;
  sla_rispettati: number;
  sla_violati: number;
  valutazione_media: number;
  ore_lavoro_totali: number;
}

const conformitaColor: Record<string, string> = {
  iso_15189: 'bg-blue-500/20 text-blue-400',
  iso_9001: 'bg-purple-500/20 text-purple-400',
  iso_27001: 'bg-orange-500/20 text-orange-400',
  nis2: 'bg-red-500/20 text-red-400',
  gdpr: 'bg-green-500/20 text-green-400',
  accreditamento_regionale: 'bg-teal-500/20 text-teal-400',
};

export default function ReportKpiContent() {
  const [tab, setTab] = useState<'kpi' | 'conformita' | 'report' | 'performance'>('kpi');
  const [kpi, setKpi] = useState<KpiSnapshot[]>([]);
  const [conformita, setConformita] = useState<ConformitaItem[]>([]);
  const [reportConfig, setReportConfig] = useState<ReportConfig[]>([]);
  const [performance, setPerformance] = useState<PerformanceTecnico[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroConformita, setFiltroConformita] = useState('');
  const [showAddReport, setShowAddReport] = useState(false);
  const [formReport, setFormReport] = useState({ nome: '', tipo: 'inventario', frequenza: 'mensile', formato: 'pdf', destinatari_email: '' });
  const [saving, setSaving] = useState(false);
  const [generatingSnapshot, setGeneratingSnapshot] = useState(false);
  const [reportBackendInfo, setReportBackendInfo] = useState<ReportConfig | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: k }, { data: c }, { data: r }, { data: p }] = await Promise.all([
      supabase.from('kpi_snapshot').select('*').order('data_snapshot', { ascending: false }).limit(10),
      supabase.from('conformita_checklist').select('*').order('tipo'),
      supabase.from('report_config').select('*').order('nome'),
      supabase.from('performance_tecnici').select('*').order('ticket_risolti', { ascending: false }),
    ]);
    setKpi(k || []);
    setConformita(c || []);
    setReportConfig(r || []);
    setPerformance(p || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleConformita = async (id: string, conforme: boolean) => {
    await supabase.from('conformita_checklist').update({ conforme: !conforme, data_verifica: new Date().toISOString() }).eq('id', id);
    fetchData();
  };

  const toggleReport = async (id: string, attivo: boolean) => {
    await supabase.from('report_config').update({ attivo: !attivo }).eq('id', id);
    fetchData();
  };

  const creaReport = async () => {
    if (!formReport.nome.trim()) { alert('Nome obbligatorio'); return; }
    // Validate email list
    const emails = formReport.destinatari_email.split(',').map(e => e.trim()).filter(Boolean);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(e => !emailRegex.test(e));
    if (invalidEmails.length > 0) { alert(`Email non valide: ${invalidEmails.join(', ')}`); return; }
    setSaving(true);
    const { error } = await supabase.from('report_config').insert({
      ...formReport,
      destinatari_email: emails,
      attivo: true,
    });
    if (error) { alert('Errore: ' + error.message); } else { alert('Report configurato'); }
    setShowAddReport(false);
    fetchData();
    setSaving(false);
  };

  const eliminaReport = async (id: string, nome: string) => {
    if (!confirm(`Eliminare la configurazione report "${nome}"?`)) return;
    const { error } = await supabase.from('report_config').delete().eq('id', id);
    if (error) { alert('Errore: ' + error.message); } else { fetchData(); }
  };

  const generaSnapshot = async () => {
    setGeneratingSnapshot(true);
    try {
      await supabase.rpc('populate_kpi_snapshot');
      await fetchData();
    } catch (e: any) {
      console.error('Errore generazione snapshot:', e.message);
    } finally {
      setGeneratingSnapshot(false);
    }
  };

  const eseguiReport = async (report: ReportConfig) => {
    // Mark ultima_esecuzione — actual generation requires a backend service
    await supabase.from('report_config').update({ ultima_esecuzione: new Date().toISOString() }).eq('id', report.id);
    fetchData();
    setReportBackendInfo(report);
  };

  const esportaCSV = async (tipo: string) => {
    try {
      let data: any[] = [];
      let headers: string[] = [];
      let rows: string[][] = [];

      if (tipo === 'inventario') {
        const { data: devices, error } = await supabase.from('devices').select('hostname, ip_address, categoria, stato, reparto, modello, numero_seriale, firmware, ultima_visita').order('hostname');
        if (error) throw error;
        headers = ['Hostname', 'IP', 'Categoria', 'Stato', 'Reparto', 'Modello', 'Seriale', 'Firmware', 'Ultima Visita'];
        rows = (devices || []).map(d => [
          d.hostname || '', d.ip_address || '', d.categoria || '', d.stato || '',
          d.reparto || '', d.modello || '', d.numero_seriale || '', d.firmware || '',
          d.ultima_visita ? new Date(d.ultima_visita).toLocaleDateString('it-IT') : '',
        ]);
      } else if (tipo === 'ticket') {
        const { data: tickets, error } = await supabase.from('tickets').select('numero_ticket, titolo, tipo, stato, priorita, reparto, aperto_da_nome, assegnato_a_nome, sla_stato, created_at, data_risoluzione').order('created_at', { ascending: false });
        if (error) throw error;
        headers = ['Numero', 'Titolo', 'Tipo', 'Stato', 'Priorità', 'Reparto', 'Aperto da', 'Assegnato a', 'SLA', 'Creato', 'Risolto'];
        rows = (tickets || []).map(t => [
          t.numero_ticket || '', t.titolo || '', t.tipo || '', t.stato || '',
          t.priorita || '', t.reparto || '', t.aperto_da_nome || '', t.assegnato_a_nome || '',
          t.sla_stato || '',
          t.created_at ? new Date(t.created_at).toLocaleDateString('it-IT') : '',
          t.data_risoluzione ? new Date(t.data_risoluzione).toLocaleDateString('it-IT') : '',
        ]);
      } else if (tipo === 'allarmi') {
        const { data: allarmi, error } = await supabase.from('allarmi').select('tipo, titolo, priorita, stato, created_at, risolto_at, note_risoluzione').order('created_at', { ascending: false });
        if (error) throw error;
        headers = ['Tipo', 'Titolo', 'Priorità', 'Stato', 'Creato', 'Risolto', 'Note'];
        rows = (allarmi || []).map(a => [
          a.tipo || '', a.titolo || '', a.priorita || '', a.stato || '',
          a.created_at ? new Date(a.created_at).toLocaleDateString('it-IT') : '',
          a.risolto_at ? new Date(a.risolto_at).toLocaleDateString('it-IT') : '',
          a.note_risoluzione || '',
        ]);
      }

      const csvLines = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
      const csv = '\uFEFF' + csvLines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${tipo}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert('Errore esportazione: ' + err.message);
    }
  };

  const latestKpi = kpi[0];
  const filteredConformita = conformita.filter(c => !filtroConformita || c.tipo === filtroConformita);
  const tipiConformita = [...new Set(conformita.map(c => c.tipo))];

  const conformitaByTipo = tipiConformita.map(tipo => {
    const items = conformita.filter(c => c.tipo === tipo);
    const conformi = items.filter(c => c.conforme).length;
    return { tipo, totale: items.length, conformi, percentuale: items.length > 0 ? Math.round((conformi / items.length) * 100) : 0 };
  });

  const chartData = kpi.slice(0, 7).reverse().map(k => ({
    data: new Date(k.data_snapshot).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
    online: k.dispositivi_online,
    offline: k.dispositivi_offline,
    sla: k.sla_rispettati_percentuale,
  }));

  return (
    <div className="flex flex-col h-full">
      <Breadcrumb items={[{ label: 'Report e KPI' }]} />
      <div className="flex gap-1 mb-4 bg-surface-elevated rounded-xl p-1 w-fit flex-wrap">
        {(['kpi', 'conformita', 'report', 'performance'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t ? 'bg-sky-600 text-white' : 'text-muted-color hover:text-white'}`}>
            {t === 'kpi' ? 'Dashboard KPI' : t === 'conformita' ? 'Conformità' : t === 'report' ? 'Report Schedulati' : 'Performance Tecnici'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {tab === 'kpi' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex gap-2">
                  <button
                    onClick={() => esportaCSV('inventario')}
                    className="flex items-center gap-2 bg-surface border border-border hover:border-sky-500/40 text-secondary-color hover:text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                  >
                    <Icon name="ArrowDownTrayIcon" size={15} />
                    CSV Inventario
                  </button>
                  <button
                    onClick={() => esportaCSV('ticket')}
                    className="flex items-center gap-2 bg-surface border border-border hover:border-sky-500/40 text-secondary-color hover:text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                  >
                    <Icon name="ArrowDownTrayIcon" size={15} />
                    CSV Ticket
                  </button>
                  <button
                    onClick={() => esportaCSV('allarmi')}
                    className="flex items-center gap-2 bg-surface border border-border hover:border-sky-500/40 text-secondary-color hover:text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                  >
                    <Icon name="ArrowDownTrayIcon" size={15} />
                    CSV Allarmi
                  </button>
                </div>
                <button
                  onClick={generaSnapshot}
                  disabled={generatingSnapshot}
                  className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  <Icon name={generatingSnapshot ? 'ArrowPathIcon' : 'BoltIcon'} size={16} className={generatingSnapshot ? 'animate-spin' : ''} />
                  {generatingSnapshot ? 'Generazione...' : 'Genera Snapshot Ora'}
                </button>
              </div>
              {latestKpi ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Dispositivi Online', value: `${latestKpi.dispositivi_online}/${latestKpi.totale_dispositivi}`, color: 'text-green-400', icon: 'ServerIcon' },
                    { label: 'Ticket Aperti', value: latestKpi.ticket_aperti, color: 'text-amber-400', icon: 'TicketIcon' },
                    { label: 'SLA Rispettati', value: `${latestKpi.sla_rispettati_percentuale}%`, color: 'text-sky-400', icon: 'ClockIcon' },
                    { label: 'Uptime Medio', value: `${latestKpi.uptime_medio_percentuale}%`, color: 'text-green-400', icon: 'ChartBarIcon' },
                    { label: 'Costo Manutenzione', value: `€${latestKpi.costo_manutenzione_mese?.toLocaleString('it-IT')}`, color: 'text-orange-400', icon: 'CurrencyEuroIcon' },
                    { label: 'Valore Parco', value: `€${latestKpi.valore_parco_macchine?.toLocaleString('it-IT')}`, color: 'text-purple-400', icon: 'BuildingOfficeIcon' },
                    { label: 'Budget Residuo', value: `€${latestKpi.budget_residuo?.toLocaleString('it-IT')}`, color: 'text-teal-400', icon: 'BanknotesIcon' },
                    { label: 'Allarmi Attivi', value: latestKpi.allarmi_attivi, color: 'text-red-400', icon: 'BellAlertIcon' },
                  ].map(s => (
                    <div key={s.label} className="bg-surface-elevated border border-border rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name={s.icon as any} size={16} className={s.color} />
                        <span className="text-xs text-muted-color">{s.label}</span>
                      </div>
                      <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-muted-color">
                  <Icon name="ChartBarIcon" size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="font-semibold">Nessun snapshot KPI disponibile</p>
                  <p className="text-sm mt-1">Clicca &quot;Genera Snapshot Ora&quot; per creare il primo snapshot</p>
                </div>
              )}
              {chartData.length > 1 && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-surface-elevated border border-border rounded-xl p-4">
                    <p className="text-sm font-semibold text-white mb-3">Dispositivi Online/Offline</p>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
                        <XAxis dataKey="data" tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px' }} />
                        <Bar dataKey="online" fill="#22c55e" name="Online" radius={[4,4,0,0]} />
                        <Bar dataKey="offline" fill="#ef4444" name="Offline" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="bg-surface-elevated border border-border rounded-xl p-4">
                    <p className="text-sm font-semibold text-white mb-3">SLA Rispettati %</p>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
                        <XAxis dataKey="data" tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <YAxis domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px' }} />
                        <Line type="monotone" dataKey="sla" stroke="#38bdf8" strokeWidth={2} dot={{ fill: '#38bdf8' }} name="SLA %" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'conformita' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {conformitaByTipo.map(c => (
                  <div key={c.tipo} className="bg-surface-elevated border border-border rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${conformitaColor[c.tipo] || 'bg-gray-500/20 text-gray-400'}`}>{c.tipo.replace(/_/g, ' ').toUpperCase()}</span>
                      <span className={`text-sm font-bold ${c.percentuale >= 80 ? 'text-green-400' : c.percentuale >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{c.percentuale}%</span>
                    </div>
                    <div className="w-full bg-surface rounded-full h-2">
                      <div className={`h-2 rounded-full ${c.percentuale >= 80 ? 'bg-green-500' : c.percentuale >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${c.percentuale}%` }} />
                    </div>
                    <p className="text-xs text-muted-color mt-1">{c.conformi}/{c.totale} conformi</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <select value={filtroConformita} onChange={e => setFiltroConformita(e.target.value)} className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
                  <option value="">Tutti i tipi</option>
                  {tipiConformita.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').toUpperCase()}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                {filteredConformita.map(c => (
                  <div key={c.id} className="bg-surface-elevated border border-border rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => toggleConformita(c.id, c.conforme)} className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${c.conforme ? 'bg-green-500 text-white' : 'border-2 border-border hover:border-green-500'}`}>
                        {c.conforme && <Icon name="CheckIcon" size={12} />}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${conformitaColor[c.tipo] || ''}`}>{c.tipo.replace(/_/g, ' ')}</span>
                          <p className="text-sm text-white">{c.voce}</p>
                        </div>
                        {c.note && <p className="text-xs text-muted-color mt-0.5">{c.note}</p>}
                      </div>
                      <span className={`text-xs font-semibold ${c.conforme ? 'text-green-400' : 'text-red-400'}`}>{c.conforme ? '✓ Conforme' : '✗ Non conforme'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'report' && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 bg-surface-elevated border border-amber-500/20 rounded-xl px-4 py-3 mb-2">
                <Icon name="ExclamationTriangleIcon" size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-secondary-color">
                  <span className="font-semibold text-amber-400">Generazione automatica non disponibile.</span>{' '}
                  La produzione di file PDF/Excel e l&apos;invio via email richiedono un servizio backend (Supabase Edge Function o cron job). 
                  Il pulsante &quot;Genera ora&quot; registra l&apos;esecuzione nel database ma non produce file.
                </p>
              </div>
              <div className="flex justify-end">
                <button onClick={() => setShowAddReport(true)} className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
                  <Icon name="PlusIcon" size={16} /> Nuovo Report
                </button>
              </div>
              {reportConfig.map(r => (
                <div key={r.id} className="bg-surface-elevated border border-border rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${r.attivo ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>{r.attivo ? 'Attivo' : 'Inattivo'}</span>
                        <span className="text-xs bg-surface text-muted-color px-2 py-0.5 rounded-full">{r.frequenza}</span>
                        <span className="text-xs bg-surface text-muted-color px-2 py-0.5 rounded-full">{r.formato.toUpperCase()}</span>
                      </div>
                      <p className="text-sm font-semibold text-white">{r.nome}</p>
                      <p className="text-xs text-muted-color mt-0.5">{r.destinatari_email?.join(', ')}</p>
                      {r.ultima_esecuzione && <p className="text-xs text-muted-color mt-0.5">Ultima esecuzione: {new Date(r.ultima_esecuzione).toLocaleString('it-IT')}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleReport(r.id, r.attivo)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${r.attivo ? 'bg-green-500' : 'bg-gray-600'}`}>
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${r.attivo ? 'translate-x-5' : 'translate-x-1'}`} />
                      </button>
                      <button onClick={() => eseguiReport(r)} className="text-xs bg-surface hover:bg-surface-elevated border border-border text-white px-3 py-1.5 rounded-lg transition-colors">Esegui</button>
                      <button onClick={() => eliminaReport(r.id, r.nome)} className="p-1.5 text-muted-color hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Elimina">
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'performance' && (
            <div className="space-y-3">
              {performance.length === 0 ? <div className="text-center py-12 text-muted-color">Nessun dato performance disponibile</div> :
              performance.map(p => (
                <div key={p.id} className="bg-surface-elevated border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-sky-500/20 flex items-center justify-center">
                        <span className="text-sm font-bold text-sky-400">{p.tecnico_nome.slice(0, 2).toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{p.tecnico_nome}</p>
                        <div className="flex gap-3 text-xs text-muted-color mt-0.5">
                          <span>{p.ticket_risolti}/{p.ticket_assegnati} risolti</span>
                          <span>• {p.tempo_medio_risoluzione_ore}h media</span>
                          <span>• SLA: {p.sla_rispettati} ok / {p.sla_violati} violati</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {p.valutazione_media > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-amber-400">{'★'.repeat(Math.round(p.valutazione_media))}</span>
                          <span className="text-sm font-semibold text-white">{p.valutazione_media.toFixed(1)}</span>
                        </div>
                      )}
                      <p className="text-xs text-muted-color">{p.ore_lavoro_totali}h totali</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showAddReport && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Nuovo Report Schedulato</h3>
              <button onClick={() => setShowAddReport(false)}><Icon name="XMarkIcon" size={20} className="text-muted-color" /></button>
            </div>
            <div className="space-y-3">
              <input value={formReport.nome} onChange={e => setFormReport(p => ({ ...p, nome: e.target.value }))} placeholder="Nome report *" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color" />
              <div className="grid grid-cols-3 gap-3">
                <select value={formReport.tipo} onChange={e => setFormReport(p => ({ ...p, tipo: e.target.value }))} className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
                  {['inventario','ticket','manutenzione','kpi','conformita','incidente','audit','tco'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={formReport.frequenza} onChange={e => setFormReport(p => ({ ...p, frequenza: e.target.value }))} className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
                  {['giornaliero','settimanale','mensile','annuale'].map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <select value={formReport.formato} onChange={e => setFormReport(p => ({ ...p, formato: e.target.value }))} className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
                  {['pdf','excel','csv'].map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                </select>
              </div>
              <input value={formReport.destinatari_email} onChange={e => setFormReport(p => ({ ...p, destinatari_email: e.target.value }))} placeholder="Email destinatari (separati da virgola)" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color" />
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowAddReport(false)} className="flex-1 border border-border text-secondary-color py-2 rounded-lg text-sm font-semibold">Annulla</button>
              <button onClick={creaReport} disabled={saving} className="flex-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-semibold">{saving ? 'Salvataggio...' : 'Crea'}</button>
            </div>
          </div>
        </div>
      )}

      {reportBackendInfo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="InformationCircleIcon" size={20} className="text-sky-400" />
                <h3 className="text-lg font-bold text-white">Esecuzione Registrata</h3>
              </div>
              <button onClick={() => setReportBackendInfo(null)}><Icon name="XMarkIcon" size={20} className="text-muted-color" /></button>
            </div>
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={16} className="text-green-400" />
                <p className="text-sm text-green-400 font-semibold">Esecuzione registrata per &quot;{reportBackendInfo.nome}&quot;</p>
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 space-y-2">
              <div className="flex items-start gap-2">
                <Icon name="ExclamationTriangleIcon" size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-400 mb-1">Generazione file non disponibile</p>
                  <p className="text-xs text-secondary-color">
                    La produzione del file <span className="font-semibold text-white">{reportBackendInfo.formato.toUpperCase()}</span> e 
                    l&apos;invio via email richiedono un servizio backend non ancora configurato.
                  </p>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-xs text-muted-color">
                <p className="font-semibold text-secondary-color">Per abilitare la generazione automatica:</p>
                <div className="flex items-center gap-2"><Icon name="CheckIcon" size={12} className="text-muted-color" /><span>Configurare una Supabase Edge Function per la generazione PDF/Excel</span></div>
                <div className="flex items-center gap-2"><Icon name="CheckIcon" size={12} className="text-muted-color" /><span>Configurare un servizio SMTP (es. SendGrid) per l&apos;invio email</span></div>
                <div className="flex items-center gap-2"><Icon name="CheckIcon" size={12} className="text-muted-color" /><span>Impostare un pg_cron job per l&apos;esecuzione schedulata ({reportBackendInfo.frequenza})</span></div>
              </div>
            </div>
            <button onClick={() => setReportBackendInfo(null)} className="w-full mt-4 bg-sky-600 hover:bg-sky-500 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors">
              Capito
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
